import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { Check, ArrowRight } from 'lucide-react';
import { db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { showNotification } from '@/lib/generalNotifications';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [companyData, setCompanyData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Log component mount for debugging
  useEffect(() => {
    console.log('Onboarding component mounted', { 
      path: window.location.pathname,
      user: user ? { uid: user.uid, email: user.email } : 'No user'
    });
    
    return () => {
      console.log('Onboarding component unmounted');
    };
  }, []);

  useEffect(() => {
    // If no user, redirect to login
    if (!user) {
      console.log('No authenticated user found, redirecting to login');
      navigate('/login');
      return;
    }
    
    console.log('Authenticated user found', { 
      uid: user.uid, 
      email: user.email,
      companyId: user.companyId
    });

    // Fetch company data
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        console.log('Fetching company data for ID:', user.companyId);
        const companyDoc = await getDoc(doc(db, 'companies', user.companyId));
        
        if (companyDoc.exists()) {
          console.log('Company data found:', companyDoc.data());
          setCompanyData(companyDoc.data());
        } else {
          console.error('Company document does not exist for ID:', user.companyId);
          showNotification({
            title: 'Error',
            message: 'Could not find company data. Please contact support.',
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [user, navigate]);

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleComplete = () => {
    showNotification({
      title: 'Welcome!',
      message: 'Your setup is complete. You can now start using the platform.',
      type: 'success'
    });
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-primary">Wikipayit</span>
            </div>
            <Button variant="ghost" onClick={handleSkip}>
              Skip Setup
            </Button>
          </div>
        </div>
      </header>

      {/* Progress steps */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center mb-8">
          <div className="flex items-center w-full max-w-lg">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > 1 ? <Check className="h-5 w-5" /> : 1}
            </div>
            <div className={`flex-1 h-1 mx-2 ${
              currentStep > 1 ? 'bg-primary' : 'bg-gray-200'
            }`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > 2 ? <Check className="h-5 w-5" /> : 2}
            </div>
            <div className={`flex-1 h-1 mx-2 ${
              currentStep > 2 ? 'bg-primary' : 'bg-gray-200'
            }`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              3
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="max-w-2xl mx-auto">
          {currentStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-center text-2xl">Welcome to Wikipayit!</CardTitle>
                <CardDescription className="text-center">
                  Thanks for starting your free trial. Let's get you set up for success.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-medium text-lg mb-2">Your Account Details</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {user?.displayName}</p>
                    <p><span className="font-medium">Email:</span> {user?.email}</p>
                    <p><span className="font-medium">Company:</span> {companyData?.name}</p>
                    <p><span className="font-medium">Plan:</span> Free Trial (30 days)</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-lg mb-2">What you can do with Wikipayit:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Create and send professional invoices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Accept online payments securely</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Track invoice status and payments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Manage your customers and products</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button onClick={handleNext}>
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          )}

          {currentStep === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-center text-2xl">Set Up Your Business</CardTitle>
                <CardDescription className="text-center">
                  Personalize your account with your business information.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="later">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="now">Set Up Now</TabsTrigger>
                    <TabsTrigger value="later">Set Up Later</TabsTrigger>
                  </TabsList>
                  <TabsContent value="now">
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Add your company details, logo, and payment settings in the Settings page.
                        This will help you create professional invoices and get paid faster.
                      </p>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => navigate('/settings?tab=profile')}
                      >
                        Go to Settings
                      </Button>
                    </div>
                  </TabsContent>
                  <TabsContent value="later">
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        You can complete your business setup later from the Settings page.
                        For now, let's continue with your onboarding.
                      </p>
                      <div className="bg-blue-50 border border-blue-100 rounded-md p-4">
                        <p className="text-sm text-blue-700">
                          <strong>Tip:</strong> Adding your company information and logo helps create more professional invoices for your customers.
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  Back
                </Button>
                <Button onClick={handleNext}>
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          )}

          {currentStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-center text-2xl">Ready to Get Started</CardTitle>
                <CardDescription className="text-center">
                  Your account is set up and ready to use.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-green-50 border border-green-100 rounded-md p-4">
                  <p className="text-green-700 font-medium">
                    Your 30-day free trial has started. No credit card required.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-lg mb-3">Quick Start Guide</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 bg-primary/10 text-primary font-medium h-7 w-7 rounded-full flex items-center justify-center">
                        1
                      </div>
                      <div>
                        <p className="font-medium">Add your first customer</p>
                        <p className="text-sm text-gray-600">
                          Create customer profiles to easily send invoices and track payments.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 bg-primary/10 text-primary font-medium h-7 w-7 rounded-full flex items-center justify-center">
                        2
                      </div>
                      <div>
                        <p className="font-medium">Set up your products or services</p>
                        <p className="text-sm text-gray-600">
                          Add items you offer to quickly add them to invoices.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 bg-primary/10 text-primary font-medium h-7 w-7 rounded-full flex items-center justify-center">
                        3
                      </div>
                      <div>
                        <p className="font-medium">Create your first invoice</p>
                        <p className="text-sm text-gray-600">
                          Generate and send a professional invoice to your customer.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  Back
                </Button>
                <Button onClick={handleComplete}>
                  Go to Dashboard
                </Button>
              </CardFooter>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
} 